import React, { ComponentProps } from "react";
import { useLocation } from "react-router-dom";
import { useSignalR } from "sonobello.utilities.react.signalr";

import AppContextValueProvider from "../../App/Compositions/AppContextValueProvider";
import CalendarSignalRListener, {
  ICalendarSignalRListenerConfig
} from "../../Events/Consumers/Components/CalendarSignalRListener";
import { ISignalRConnectionConfig } from "../../Events/Consumers/Components/SignalRConnection";
import CachedSignalRConnection from "../../Events/Consumers/Compositions/CachedSignalRConnection";
import usePostSignalRNegotiate from "../../Events/Consumers/Hooks/usePostSignalRNegotiate";
import { ICalendarEvent } from "../../Events/Consumers/Types/ICalendarEvent";
import IPathObserver from "../../Routing/Types/IPathObserver";
import CalendarsManager, { ICalendarWorkerProps, ICalendarsManagerProps } from "../Components/CalendarManager";
import CenterCalendarWorker from "../Components/CenterCalendarWorker";
import ICalendar from "../Types/ICalendar";
import AppContextOpsCalendarController from "./AppContextOpsCalendarController";
import AppContextUseGetBookingCenterOptions from "./AppContextUseGetBookingCenterOptions";
import AppContextUseGetOpsCenterCalendar from "./AppContextUseGetOpsCenterCalendar";

const AppContextCalendarManager = AppContextValueProvider<
  ICalendarsManagerProps,
  ComponentProps<typeof CalendarsManager>
>({
  Component: CalendarsManager,
  map: ({ patient, defaultCenter, sessionKeyCState, router, setBookingController }) => {
    return {
      businessUnitId: patient.opportunity.campaign.businessUnit.id,
      patientFirstName: patient.firstName,
      defaultCenter,
      router,
      sessionKey: sessionKeyCState,
      setBookingController
    };
  }
});

const OpsWorker: React.FC<ICalendarWorkerProps<ICalendarEvent, ICalendar>> = props => {
  const location = useLocation();
  return (
    <AppContextOpsCalendarController pathName={location.pathname}>
      <CenterCalendarWorker {...props} useGetCenterCalendar={AppContextUseGetOpsCenterCalendar} />
    </AppContextOpsCalendarController>
  );
};

const Manager: ICalendarSignalRListenerConfig["CalendarManager"] = props => (
  <AppContextCalendarManager {...props} useGetCenters={AppContextUseGetBookingCenterOptions} Worker={OpsWorker} />
);

const SignalRListener: ISignalRConnectionConfig["SignalRListener"] = props => (
  <CalendarSignalRListener {...props} CalendarManager={Manager} />
);

/** A fully configured calendar manager which uses OPS. */
const OpsSupportedCalendarManager: React.FC<
  Pick<ISignalRConnectionConfig, "router"> & Pick<ICalendarsManagerProps, "router"> & IPathObserver
> = ({ router }) => {
  return (
    <CachedSignalRConnection
      router={router}
      useGetConnectionInfo={usePostSignalRNegotiate}
      useSignalR={useSignalR}
      maxConnectionAttempts={3}
      SignalRListener={SignalRListener}
    />
  );
};

export default OpsSupportedCalendarManager;
